import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Select from 'react-select';
import {
  Button,
  ButtonGroup,
  Col,
  Row,
  ListGroup,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModalTop } from '../../../../actions';
import { showConfirmationModal } from '../../../../actions/App';

import HelpIcon from '../../../HelpIcon';
import SettingsItem from '../Item/Item';
import AdminShareSelectedTemplate from '../../ShareSelectedTemplate';
import SettingsEditButtons from '../EditButtons';
import ProcessingSpinner from '../../../ProcessingSpinner';
import ErrorBoundary from '../../../ErrorBoundary';
import TrialNotice from '../../../TrialNotice';
import UpgradePlanButton from '../../../UpgradePlanButton';
import EmptyShareSettings from '../../../../containers/EmptyContainers/ShareSettings';

import { userHasFeature } from '../../../../helpers/Features';
import { getSetting } from '../../../../helpers/Settings';

const SettingsShare = ({
  settings,
  hiddenSettings,
  saving,
  handleSettingChange,
  handleShareTemplatesAdd,
  handleShareTemplatesChange,
  handleShareTemplatesRemove,
  handleSelectedTemplateUuid,
  handleSave,
  selectedTemplateUuid,
  openConfirmationModal,
  handleModal,
  shareTemplates,
  loadingAddTemplate,
  loadingRemoveTemplate,
  unsaved,
}) => {
  const intl = useIntl();

  const viewAllowedSettings = [
    'share_templates_user_create',
  ];

  const shareTemplatesOptions = shareTemplates.map(p => ({ label: p.name, value: p.uuid }));
  const selectedTemplate = shareTemplates.length > 0 ?
    shareTemplates.find(p => p.uuid === selectedTemplateUuid) :
    null;

  useEffect(() => {
    if (selectedTemplateUuid === null && shareTemplates.length > 0) {
      handleSelectedTemplateUuid(shareTemplates[0].uuid);
    }
  }, [handleSelectedTemplateUuid, selectedTemplateUuid, shareTemplates]);

  const handleChangeTemplate = (option) => {
    handleSelectedTemplateUuid(option.value);
  };

  const handleShareTemplatesDeleteWrapper = () => {
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Do you want to remove this template?' }),
      processingText: intl.formatMessage({ defaultMessage: 'Removing' }),
      onConfirm: () => {
        handleShareTemplatesRemove(selectedTemplateUuid);
        return Promise.resolve();
      },
    };
    openConfirmationModal(modalParams);
  };

  const handleShareTemplatesAddModal = () => {
    const handleConfirm = (name) => {
      handleShareTemplatesAdd(name);
    };
    handleModal(null, handleConfirm);
  };

  const handleShareTemplatesEditModal = (name, editKey) => {
    const handleConfirm = (newName, key) => {
      handleShareTemplatesChange(key, 'name', newName);
    };
    handleModal(name, handleConfirm, editKey);
  };

  return (
    <ErrorBoundary>
      <div className="settings-inner-content">
        <Row>
          <Col>
            <div className="tab-content-wrapper share-settings-tab">
              <h3>
                {intl.formatMessage({ defaultMessage: 'Share Settings' })}
                <HelpIcon url="https://help.stockpress.co/en/articles/9810537-creating-share-templates" />
                <UpgradePlanButton
                  feature="share-templates"
                />
              </h3>
              <TrialNotice feature="share-templates" className="pl-0" />
              {userHasFeature('hide-stockpress-branding-shares') && (
                <ListGroup flush className="switch-list workspace-settings-list">
                  {Object.keys(settings)
                    .filter(s => (['hide_stockpress_branding_shares'].includes(s)))
                    .filter(s => (!hiddenSettings.includes(s)))
                    .map(key => (
                      <SettingsItem
                        key={key}
                        settingKey={key}
                        setting={settings[key]}
                        handleSettingChange={handleSettingChange}
                      />
                    ))}
                </ListGroup>
              )}
              {userHasFeature('share-templates') && (
                <>
                  <ListGroup flush className="switch-list workspace-settings-list">
                    {Object.keys(settings)
                      .filter(s => (viewAllowedSettings.includes(s)))
                      .filter(s => (!hiddenSettings.includes(s)))
                      .map(key => (
                        <SettingsItem
                          key={key}
                          settingKey={key}
                          setting={settings[key]}
                          handleSettingChange={handleSettingChange}
                        />
                      ))}
                  </ListGroup>
                  <hr />
                  {getSetting('share_templates_user_create') && (
                    <>
                      <h5>
                        {intl.formatMessage({ defaultMessage: 'Templates' })}
                      </h5>
                      <div className="share-settings-templates-controls">
                        <Select
                          className="share-settings-select-templates"
                          classNamePrefix="share-settings-select-templates"
                          options={shareTemplatesOptions}
                          value={selectedTemplate ? {
                            value: selectedTemplate.uuid, label: selectedTemplate.name,
                          } : null}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          onChange={handleChangeTemplate}
                          isDisabled={!userHasFeature('share-templates')}
                        />
                        <ButtonGroup>
                          <Button
                            id="ShareSettingsEditTemplate"
                            color="primary"
                            onClick={() => handleShareTemplatesEditModal(
                              selectedTemplate.name,
                              selectedTemplateUuid,
                            )}
                            disabled={shareTemplates.length === 0 || !userHasFeature('share-templates')}
                          >
                            <i className="far fa-edit" />
                          </Button>
                          <Button
                            id="ShareSettingsRemoveTemplate"
                            color="primary"
                            onClick={() => handleShareTemplatesDeleteWrapper(selectedTemplateUuid)}
                            disabled={shareTemplates.length === 0 || loadingRemoveTemplate || !userHasFeature('share-templates')}
                          >
                            {loadingRemoveTemplate ? (
                              <ProcessingSpinner show={loadingRemoveTemplate} />
                            ) : (
                              <i className="far fa-trash" />
                            )}
                          </Button>
                        </ButtonGroup>
                        <Button
                          id="ShareSettingsAddTemplate"
                          color="primary"
                          onClick={() => handleShareTemplatesAddModal()}
                          disabled={loadingAddTemplate || !userHasFeature('share-templates')}
                        >
                          {loadingAddTemplate ? (
                            <ProcessingSpinner show={loadingAddTemplate} />
                          ) : (
                            <i className="far fa-plus" />
                          )}
                        </Button>
                      </div>

                      {selectedTemplate && (
                        <AdminShareSelectedTemplate
                          selectedTemplateUuid={selectedTemplateUuid}
                          templates={shareTemplates}
                          handleShareTemplatesChange={handleShareTemplatesChange}
                          handleSelectedTemplateUuid={handleSelectedTemplateUuid}
                        />
                      )}
                    </>
                  )}
                  {!selectedTemplate && (
                    <EmptyShareSettings handleNewItem={() => handleShareTemplatesAddModal()} />
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <SettingsEditButtons
        unsaved={unsaved}
        saving={saving}
        handleSave={handleSave}
      />
    </ErrorBoundary>
  );
};

const mapDispatchToProps = dispatch => ({
  openConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
  handleModal: (name, handleConfirm, editKey = null) => {
    dispatch(showModalTop('ShareTemplateModal', {
      name,
      handleConfirm,
      editKey,
    }));
  },
});

SettingsShare.propTypes = {
  settings: PropTypes.object.isRequired,
  hiddenSettings: PropTypes.array.isRequired,
  saving: PropTypes.bool.isRequired,
  handleSettingChange: PropTypes.func.isRequired,
  handleShareTemplatesAdd: PropTypes.func.isRequired,
  handleShareTemplatesChange: PropTypes.func.isRequired,
  handleShareTemplatesRemove: PropTypes.func.isRequired,
  handleSelectedTemplateUuid: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  selectedTemplateUuid: PropTypes.string,
  openConfirmationModal: PropTypes.func.isRequired,
  handleModal: PropTypes.func.isRequired,
  shareTemplates: PropTypes.array.isRequired,
  loadingAddTemplate: PropTypes.bool.isRequired,
  loadingRemoveTemplate: PropTypes.bool.isRequired,
  unsaved: PropTypes.bool.isRequired,
};

SettingsShare.defaultProps = {
  selectedTemplateUuid: null,
};

export default connect(null, mapDispatchToProps)(SettingsShare);
